import { CircleCheck, Trash2 } from "lucide-react";
import { FC, useMemo } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { CustomVerificationType } from "@/data/types";
import { CreateVerificationTypeDialog } from "@/dialogs/create-custom-verification-type-dialog";
import { DeleteVerificationTypeDialog } from "@/dialogs/delete-custom-verification-type-dialog";
import { useDialogState } from "@/hooks/use-dialog-state";
import {
  canCreateCustomVerificationType,
  canDeleteCustomVerificationType,
} from "@/lib/permissions";
import useStore from "@/state";

type Props = {
  verificationTypes?: CustomVerificationType[];
};

export const VerificationTypes: FC<Props> = ({ verificationTypes }) => {
  const { openDialog } = useDialogState();

  const { session } = useStore();

  const canCreateVerificationType = useMemo(() => {
    return canCreateCustomVerificationType(session?.permissions);
  }, [session]);

  const canDeleteVerificationType = useMemo(() => {
    return canDeleteCustomVerificationType(session?.permissions);
  }, [session]);

  return (
    <>
      <h3 className="scroll-m-20 text-2xl font-medium tracking-tight mt-8">
        Custom Verification Types
      </h3>

      <div className="flex justify-between items-center mt-4">
        <p>
          The following verification types are available for your organization.
        </p>
        {canCreateVerificationType && <CreateVerificationTypeDialog />}
      </div>

      <Card className="mt-6 p-6">
        <div className="space-y-4">
          {verificationTypes?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No custom verification types configured.
            </p>
          )}
          {verificationTypes?.map((verificationType) => (
            <div
              key={verificationType.id}
              className="flex justify-between items-start border-b pb-2"
            >
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium">{verificationType.name}</p>
                </div>
                <p className="text-sm mt-1">
                  {verificationType.description || "--"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to={`/verifications?customVerificationTypeId=${verificationType.id}`}
                >
                  <Button variant="outline" size="sm" className="w-[70px]">
                    <CircleCheck /> {verificationType.tag_verifications_count}
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="icon"
                  disabled={
                    !canDeleteVerificationType ||
                    verificationType.tag_verifications_count > 0
                  }
                  onClick={() => {
                    openDialog({
                      name: "delete-verification-type",
                      params: {
                        verificationTypeName: verificationType.name,
                        verificationTypeId: verificationType.id,
                      },
                    });
                  }}
                >
                  <Trash2 className="h-4 w-4 stroke-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <DeleteVerificationTypeDialog />
    </>
  );
};
